import { BaseListViewStore, ProductHyperLinkCellData, InventoryDataCell, TooltipHeaderCellData, MainViewStore } from 'core/stores';
import {
	TableFilterUtility,
	isSome,
	checkForColumnFilter,
	RemoveFiltersModalUtility,
	checkForColumnWidth,
	checkForNestedColumnWidth,
	isWarehouse,
	isSuperAdminOrganization,
	isSeller,
	isSmartshipWarehouseAdministrator,
} from 'core/utils';
import { observable, action, makeObservable } from 'mobx';
import { ProductStore, UpdateInventoryStore } from 'modules/product/stores';
import { ApplicationService, ProductService } from 'common/data';
import { localStorageHandler } from 'core/services';
import { TableFilterTemplate } from 'themes/components';
import { LookupStore } from 'modules/lookup/stores';
import { ApplicationStore } from 'platform/application/stores';
import { sourceOfChange } from 'common/constants';
import excelJS from 'exceljs';
import _ from 'lodash';
import { ExportInventoryByDateModal, SyncInventoryForSeller } from 'modules/order/pages';

class InventoryListViewStore extends BaseListViewStore {
	@observable data = [];
	@observable change = false;
	orderService = null;
	localStorageWidthKey = 'inventory-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ProductStore(rootStore),
			queryParams: {
				embed: ['seller', 'productInventories', 'productInventories.assignedWarehouse'],
				filter: localStorageHandler.get('inventory-filter'),
			},
			findFunction: 'findProductInventory',
			tableData: {
				columns: [
					{
						key: 'mainSku',
						dataCell: ProductHyperLinkCellData,
						routeName: 'master.app.main.product.list.detail',
						title: 'INVENTORY.PRODUCTMAINSKU',
						service: 'productService',
						filter: null,
						width: 200,
						filterType: TableFilterTemplate,
						sortable: true,
						selected: true,
						locked: true,
					},
					{
						key: 'name',
						title: 'INVENTORYVIEW.PRODUCTNAME',
						filter: null,
						width: 200,
						filterType: TableFilterTemplate,
						sortable: true,
						selected: true,
					},
					{
						key: 'seller.applicationTitle',
						title: 'INVENTORYVIEW.SELLER',
						service: 'applicationService',
						filter: null,
						width: 200,
						filterType: TableFilterTemplate,
						filterApiParams: {
							sellerOnly: true,
						},
						sortable: true,
						selected: true,
						dontSplit: true,
						enabled: isWarehouse(rootStore) || isSuperAdminOrganization(rootStore),
						locked: false,
					},
				],
				sortObject: [
					{
						field: 'mainSku',
						dir: 'asc',
					},
				],
				actions: {
					onProductActivity: item => this.routes.productActivity(item.id),
				},
			},
			permissions: {
				onProductActivity: () => this.authStore.hasPermission('channelSection.full'),
			},
			routes: {
				productDetail: id => this.rootStore.routerStore.goTo('master.app.main.product.list.detail', { id }),
				detail: id => this.rootStore.routerStore.goTo('master.main.channel.preview', { id }),
				edit: id => this.rootStore.routerStore.setQueryParams({ editId: id }),
				create: () => this.rootStore.routerStore.setQueryParams({ create: null }),
				inventorySync: () => this.rootStore.routerStore.goTo('master.app.main.inventory.inventorysync', {}),
				productActivity: id =>
					this.rootStore.routerStore.goTo('master.app.main.product.list.activity', {
						id,
					}),
			},
		});
		makeObservable(this);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		if (!isSeller(this.rootStore)) {
			await this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		await this.queryUtility.initialize(this.findProductInventory);
		checkForColumnFilter(localStorageHandler.get('inventory-table-data'), this.tableData.columns);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.columnFilterSetup = 'inventory-table-data';
		this.updateInventoryStore = new UpdateInventoryStore(this.rootStore);
		this.lookupStore = new LookupStore(this.rootStore);
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.isUploadAllowed = isSeller(this.rootStore) || isSuperAdminOrganization(this.rootStore);
		this.tableFilter = new TableFilterUtility({
			lookupStore: this.lookupStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [
				new ProductService(this.rootStore.app.baasic.apiClient),
				new ApplicationService(this.rootStore.app.baasic.apiClient),
			],
		});
		this.setButtons();
		this.setHeaderModes();
		this.setSearchVisible();
		this.resume();
	};

	@action.bound
	setButtons = () => {
		const buttons = {
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-trash spc--right--sml',
					onClick: () => {
						this.removeFiltersModal.handleRemoveFiltersButtonClick();
					},
					text: 'FILTER.REMOVEALL',
					disabled: !!(this.queryUtility.filter.advancedSearch === undefined),
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-handshake spc--right--sml',
					onClick: this.routes.inventorySync,
					text: 'MAINMENU.ADMINISTRATION.INVSYNC',
					authorization: 'inventorySection.full',
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-sync spc--right--sml',
					onClick: () => {
						this.onSyncInventory();
					},
					text: 'Sync Inventory',
					authorization: 'inventorySection.full',
				},
			],
		};
		if (this.isUploadAllowed) {
			buttons.buttons.push({
				className: 'btn btn--dropdown header__button',
				iconClassName: 'icon icon--xmed icon--inventory-update--primary spc--right--sml',
				onClick: () => this.openConfirmationModal(this.getUploadInventoryConfirmModalProps()),
				text: 'UPDATEINVENTORYLOG.TITLE',
			});
		}
		this.setButtonComponent(buttons);
	};

	@action.bound
	findProductInventory = async () => {
		this.suspend();
		if (this.organizationStore == null) {
			this.organizationStore = new ApplicationStore(this.rootStore);
		}
		this.queryUtility.setFilterParams(
			this,
			{
				calculateInventory: true,
				isActive: true,
				productInventoryAssignedWarehouseId: isWarehouse(this.rootStore)
					? this.rootStore.applicationStore.currentOrganization.id
					: null,
				sellerWarehousesOnlyOrganizationId: isSeller(this.rootStore)
					? this.rootStore.applicationStore.currentOrganization.id
					: isSome(this.rootStore.viewStore.sellerDropdownStore) &&
					  isSome(this.rootStore.viewStore.sellerDropdownStore.value)
					? this.rootStore.viewStore.sellerDropdownStore.value.value
					: null,
				includeOnlySellerWarehouses: true,
			},
			this.tableFilter
		);
		await Promise.all([
			this.listStore.findProduct(this.queryUtility.filter),
			this.organizationStore.find({
				page: 1,
				rpp: 500,
				warehouseOnly: true,
				isOrganizationActive: true,
				sellerWarehousesOnlyOrganizationId: isSeller(this.rootStore)
					? this.rootStore.applicationStore.currentOrganization.id
					: isSuperAdminOrganization(this.rootStore) && this.rootStore.viewStore.sellerDropdownStore.value != null
					? this.rootStore.viewStore.sellerDropdownStore.value.value
					: null,
				includeOnlySellerWarehouses: isSeller(this.rootStore)
					? true
					: isSuperAdminOrganization(this.rootStore) && this.rootStore.viewStore.sellerDropdownStore.value != null
					? true
					: false,
				ids: isWarehouse(this.rootStore) ? [this.rootStore.applicationStore.currentOrganization.id] : null,
			}),
		]);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.queryUtility.setFilterPersistance(this, 'inventory-filter', 'inventory-table-data');
		this.prepareData(this.organizationStore.response.item);
		checkForNestedColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.setButtons();
		this.resume();
	};

	@action.bound
	prepareData = warehouses => {
		this.tableData.columns = this.tableData.columns.filter(column => !column.nestedColumns);
		_.each(warehouses, warehouse => {
			this.tableData.columns.push({
				title: `${warehouse.applicationTitle}`,
				sortable: false,
				headerCell: TooltipHeaderCellData,
				translation: `${warehouse.applicationTitle}`,
				nestedColumns: [
					{
						key: `${warehouse.applicationTitle}.totalQuantity`,
						title: 'INVENTORYVIEW.ACTUAL',
						width: 90,
						dataCell: InventoryDataCell,
						headerCell: TooltipHeaderCellData,
						isTotalQuantityColumn: true,
						sortable: false,
						translation: 'INVENTORYVIEW.ACTUAL',
						warehouseKey: `${warehouse.applicationTitle}`,
						selected: true,
					},
					{
						key: `${warehouse.applicationTitle}.available`,
						title: 'INVENTORYVIEW.AVAILABLE',
						width: 60,
						dataCell: InventoryDataCell,
						headerCell: TooltipHeaderCellData,
						sortable: false,
						translation: 'INVENTORYVIEW.AVAILABLE',
						warehouseKey: `${warehouse.applicationTitle}`,
						selected: true,
					},
					{
						key: `${warehouse.applicationTitle}.safety`,
						title: 'INVENTORYVIEW.SAFETY',
						width: 60,
						sortable: false,
						dataCell: InventoryDataCell,
						headerCell: TooltipHeaderCellData,
						translation: 'INVENTORYVIEW.SAFETY',
						warehouseKey: `${warehouse.applicationTitle}`,
						selected: true,
					},
					{
						key: `${warehouse.applicationTitle}.pending`,
						title: 'INVENTORYVIEW.PENDING',
						width: 60,
						sortable: false,
						dataCell: InventoryDataCell,
						headerCell: TooltipHeaderCellData,
						translation: 'INVENTORYVIEW.PENDING',
						warehouseKey: `${warehouse.applicationTitle}`,
						selected: true,
					},
					{
						key: `${warehouse.applicationTitle}.unallocated`,
						title: 'INVENTORYVIEW.UNALLOCATED',
						width: 60,
						sortable: false,
						dataCell: InventoryDataCell,
						headerCell: TooltipHeaderCellData,
						translation: 'INVENTORYVIEW.UNALLOCATED',
						warehouseKey: `${warehouse.applicationTitle}`,
						selected: true,
					},
				],
			});
		});
		this.mapData(this.organizationStore.response.item, this.listStore.response.item);
	};

	@action.bound
	mapData = (warehouses, products) => {
		this.data = _.map(products, product => {
			var dataObject = {
				...product,
			};
			_.each(warehouses, warehouse => {
				const inventory = _.find(product.productInventories, item => {
					return item.assignedWarehouseId == warehouse.id;
				});
				if (inventory != undefined) {
					dataObject[`${warehouse.applicationTitle}`] = {
						available: isSome(inventory.available) && inventory.available > 0 ? inventory.available : 0,
						safety: isSome(inventory.safeQuantity) ? inventory.safeQuantity : 0,
						pending: isSome(inventory.pending) ? inventory.pending : 0,
						totalQuantity: isSome(inventory.quantity) ? inventory.quantity : 0,
						unallocated: isSome(inventory.unallocated) ? inventory.unallocated : 0,
					};
				} else {
					dataObject[`${warehouse.applicationTitle}`] = {
						available: 0,
						safety: 0,
						pending: 0,
						totalQuantity: 0,
						unallocated: 0,
					};
				}
			});
			return dataObject;
		}).slice();
	};

	@action.bound
	exportDataToCSV = async () => {
		this.suspend();
		const workbook = new excelJS.Workbook();
		workbook.created = new Date();
		workbook.modified = new Date();
		const sheet = workbook.addWorksheet('Current_inventory_warehouse_sku');
		sheet.columns = [
			{ header: 'SKU', key: 'sku' },
			{ header: 'Product Name', key: 'name' },
			{ header: 'Warehouse', key: 'warehouse' },
			{ header: 'Available Quantity', key: 'available' },
			{ header: 'Safety Quantity', key: 'safety' },
			{ header: 'Pending Quantity', key: 'pending' },
			{ header: 'Total Quantity', key: 'totalQuantity' },
			{ header: 'Unallocated Quantity', key: 'unallocated' },
		];

		let allRecords = await this.listStore.findProduct({
			...this.queryUtility.filter,
			pageSize: this.listStore.response.totalRecords,
			pageNumber: 1,
		});

		_.each(allRecords.item, product => {
			_.each(this.organizationStore.response.item, warehouse => {
				const inventory = _.find(product.productInventories, productInventory => {
					return productInventory.assignedWarehouseId === warehouse.id;
				});
				if (isSome(inventory)) {
					const row = {
						sku: product.mainSku,
						name: product.name,
						warehouse: warehouse.applicationTitle,
						totalQuantity: isSome(inventory.quantity) ? inventory.quantity : 0,
						available: isSome(inventory.available) && inventory.available > 0 ? inventory.available : 0,
						safety: isSome(inventory.safeQuantity) ? inventory.safeQuantity : 0,
						pending: isSome(inventory.pending) ? inventory.pending : 0,
						unallocated: isSome(inventory.unallocated) ? inventory.unallocated : 0,
					};
					sheet.addRow(row);
				}
			});
		});
		workbook.xlsx.writeBuffer().then(function(buffer) {
			const blob = new Blob([buffer], { type: 'application/xlsx' });
			saveAs(blob, 'Current_inventory_warehouse_sku.xlsx');
		});
		this.rootStore.notificationStore.success('EXPORTTOXLS.SUCCESS');
		this.resume();
	};

	getUploadInventoryConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.updateInventoryForAllChannelsAndProducts(),
			message: ['UPDATEINVENTORYLOG.ALLCONFIRMINVUPLOAD'],
		};
	};

	@action.bound
	async updateInventoryForAllChannelsAndProducts() {
		this.suspend();
		try {
			const result = await this.updateInventoryStore.updateInventoryForAllChannelsAndProducts(
				sourceOfChange.inventoryListUpdateInventoryLog
			);
			result
				? this.rootStore.notificationStore.success(['UPDATEINVENTORYLOG.ALLSUCCESS'])
				: this.rootStore.notificationStore.error([
						'UPDATEINVENTORYLOG.ALLNOTHINGTOUPLOAD',
						{ name: this.product.mainSku },
				  ]);
		} catch (error) {
			this.rootStore.notificationStore.error(['UPDATEINVENTORYLOG.ALLERROR']);
		}
		this.resume();
	}

	@action.bound
	onSyncInventory = async () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			component: SyncInventoryForSeller,
		});
	}

	@action.bound
	onExportByDate = async () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			component: ExportInventoryByDateModal,
		});
	}
}

export default InventoryListViewStore;
